import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { HeaderRow } from '../HeaderRow/HeaderRow';
import { TableRow } from '../TableRow/TableRow';
import { formatCurrency, formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { Lozenge } from '../../../../../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { Table } from './CompareTable.styles';
import { CompareTableLoading } from './CompareTable.loading';
import { ErrorState } from '../../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { PieChart } from '../../../../../../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { useTheme } from '../../../../../../../../theme/theme';
import { usePlans } from '../../../../../../hooks/usePlans';
import { Footer } from '../Footer/Footer';
import { renderAges } from '../../utils/renderAges';
import { PdfPageWrapper } from '../../../../../../../../PDFRenderer/PDFRenderer.styles';
import { selectIsCanadian } from '../../../../../../../../CommonSelectors/CommonAreaSelector';
import { Separator } from '../../../../../../../../CommonComponents/OPL/Separator/Separator';
import { PDFTitle } from '../../../../../../../../PDFRenderer/components/PDFTitle/PDFTitle';
import { useCompareTable } from './useCompareTable';

interface Props {
  clientId: string;
  planIds: string[];
  goalId: string;
  isPrint?: boolean;
  title?: string;
}

export const CompareTable = ({ clientId, planIds, goalId, isPrint, title }: Props) => {
  const { isLoading, isError, refetch, data } = useCompareTable({ clientId, planIds });

  const plans = usePlans({ clientId });

  const theme = useTheme();

  const isCanadian = useSelector(selectIsCanadian);

  const planNames = planIds.map(planId => {
    const plan = plans.find(({ planId: _planId }) => planId === _planId);

    return plan ? plan.name : '';
  });

  if (isLoading) {
    return <CompareTableLoading planNames={planNames} />;
  }

  if (isError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" mt="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const Wrapper = isPrint ? PdfPageWrapper : React.Fragment;

  return (
    <Wrapper>
      {isPrint && (
        <>
          <PDFTitle title={title} />

          <Text as="h2" fontSize="h5" fontWeight={600}>
            {`${planIds.length} Scenarios Selected`}
          </Text>

          <Separator mb="24" />
        </>
      )}

      <Table>
        <thead css="display:table-header-group">
          <tr>
            <Box as="th" p="24">
              {isPrint && <Text color="white">Empty Cell</Text>}
            </Box>

            {planNames.map(planName => (
              <Box key={planName} as="th" p="24">
                <Text fontSize="h5" fontWeight={600} textAlign="center" isOverflowWrap>
                  {planName}
                </Text>
              </Box>
            ))}
          </tr>
        </thead>

        <tbody>
          <HeaderRow label="Overview" planIds={planIds} isPrint={isPrint} />

          <TableRow
            valueKey="totalNetWorthAtRetirement"
            label="Total Net Worth at Retirement"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontSize="h5" fontWeight={600} textAlign="center">
                {formatCurrency(value)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="totalNetWorthEndOfPlan"
            label="Total Net Worth at End of Plan"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatCurrency(value)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="isGoalFunded"
            label="Funding Account Status"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                <Lozenge variantColor={value ? 'primary' : 'default'}>
                  {value ? 'Funded' : 'Not Funded'}
                </Lozenge>
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="probabilityOfSuccess"
            label="Probability of Success"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                <Box width={92} height={92} mx="auto">
                  <PieChart
                    series={[
                      {
                        data: [
                          {
                            name: 'Success',
                            y: value,
                            color:
                              value >= 0.7 ? theme.colors.success[500] : theme.colors.error[600],
                          },
                          {
                            name: 'Failure',
                            y: 1 - value,
                            color: theme.colors.neutral[200],
                          },
                        ],
                      },
                    ]}
                    title={formatPercentage(value)}
                    customSettings={{
                      title: { y: 18 },
                      plotOptions: {
                        series: { animation: !isPrint },
                      },
                    }}
                  />
                  {isPrint && <Text css="font-size: 1px;color: #fff">Empty Cell</Text>}
                </Box>
              </Text>
            )}
          </TableRow>

          <HeaderRow label="Key Assumptions" planIds={planIds} isPrint={isPrint} />

          <TableRow
            valueKey="retirementAgePrincipal"
            spouseValueKey="retirementAgeSpouse"
            label="Retirement Age"
            planIds={planIds}
            comparedPlans={data}
          >
            {(principalAge, spouseAge) => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {renderAges(principalAge, spouseAge)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="lifeExpectancyPrincipal"
            spouseValueKey="lifeExpectancySpouse"
            label="Life Expectancy"
            planIds={planIds}
            comparedPlans={data}
          >
            {(principalAge, spouseAge) => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {renderAges(principalAge, spouseAge)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="livingExpense"
            label="Living Expense"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatCurrency(value)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="additionalSavingPerMonth"
            label="Additional Savings Per Month"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatCurrency(value)}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="earmarkedAssetsAtRetirement"
            label="Earmarked Assets at Retirement"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatCurrency(value)}
              </Text>
            )}
          </TableRow>

          <HeaderRow label="Assumptions" planIds={planIds} isPrint={isPrint} />

          {!isCanadian && (
            <>
              <TableRow
                valueKey="socialSecurityFilingAge"
                spouseValueKey="socialSecurityFilingAgeSpouse"
                label="Social Security Filing Age"
                planIds={planIds}
                comparedPlans={data}
              >
                {(principalAge, spouseAge) => (
                  <Text variant="tabular" fontWeight={500} textAlign="center">
                    {renderAges(principalAge, spouseAge)}
                  </Text>
                )}
              </TableRow>

              <TableRow
                valueKey="totalLifetimeSocialSecurityBenefits"
                label="Total Lifetime Social Security Benefits"
                planIds={planIds}
                comparedPlans={data}
              >
                {value => (
                  <Text variant="tabular" fontWeight={500} textAlign="center">
                    {formatCurrency(value)}
                  </Text>
                )}
              </TableRow>
            </>
          )}

          <TableRow
            valueKey="inflationRate"
            label="Inflation Rate"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatPercentage(value, { decimalPlaces: 1 })}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="preRetirementAssetAllocation"
            label="Pre-Retirement Asset Allocation"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => {
              return (
                <Text variant="tabular" fontWeight={500} textAlign="center">
                  {value}
                </Text>
              );
            }}
          </TableRow>

          <TableRow
            valueKey="preRetirementRateOfReturn"
            label="Pre-Retirement Rate of Return"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatPercentage(value, {
                  decimalPlaces: 1,
                })}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="preRetirementStandardDeviation"
            label="Pre-Retirement Standard Deviation"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatPercentage(value, {
                  decimalPlaces: 2,
                })}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="postRetirementAssetAllocation"
            label="Post-Retirement Asset Allocation"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {value}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="postRetirementRateOfReturn"
            label="Post-Retirement Rate of Return"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatPercentage(value, {
                  decimalPlaces: 1,
                })}
              </Text>
            )}
          </TableRow>

          <TableRow
            valueKey="postRetirementStandardDeviation"
            label="Post-Retirement Standard Deviation"
            planIds={planIds}
            comparedPlans={data}
          >
            {value => (
              <Text variant="tabular" fontWeight={500} textAlign="center">
                {formatPercentage(value, {
                  decimalPlaces: 2,
                })}
              </Text>
            )}
          </TableRow>
        </tbody>
      </Table>

      {!isPrint && <Footer clientId={clientId} planIds={planIds} goalId={goalId} />}
    </Wrapper>
  );
};
